import type React from 'react';
import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import Button, { ButtonColorEnum, ButtonSizeEnum, ButtonVariantEnum } from 'components/stateless/Button/Button';
import Pagination from 'components/stateless/Pagination/Pagination';
import SearchBorder from 'components/stateless/SearchForm/SearchBorder';
import SearchDatePicker from 'components/stateless/SearchForm/SearchDatePicker';
import SearchInput from 'components/stateless/SearchForm/SearchInput';
import SearchLabel from 'components/stateless/SearchForm/SearchLabel';
import SearchSelect from 'components/stateless/SearchForm/SearchSelect';
import TableBody from 'components/stateless/Table/TableBody';
import TableBorder from 'components/stateless/Table/TableBorder';
import type { HeaderType } from 'components/stateless/Table/TableHeader';
import TableHeader from 'components/stateless/Table/TableHeader';
import Td from 'components/stateless/Table/Td';
import Tr from 'components/stateless/Table/Tr';
import { HeaderTitle } from 'components/stateless/Title/HeaderTitle';
import { SectionTitle } from 'components/stateless/Title/SectionTitle';
import getSelectOptions from 'constants/selectOptions';
import useMounted from 'hooks/useMounted';
import usePageable from 'hooks/usePageable';
import useProperty from 'hooks/useProperty';
import type Pageable from 'models/Pageable';
import type { InvoicePaymentVOModel } from 'models/vo/InvoicePaymentVO';
import { setFormValues } from 'utils/form/setFormValues';
import { requestAnchorInvoicePaymentList } from 'utils/http/api/anchor/invoice-payments';
import type { AnchorInvoicePaymentListRequest } from 'utils/http/api/anchor/invoice-payments/requests';
import useModal from 'utils/modal/useModal';
import { getParsedSearchParams, updateSearchParams } from 'utils/searchParams';

const getConstants = () => {
  const mounted = useMounted();
  const modal = useModal();
  const { t } = useTranslation(['format']);
  const AC_INVOICE_PAYMENT_LIST_QS_KEY = 'ac-invoice-payment-list';
  const SEARCHED_ANCHOR_INVOICE_PAYMENT_LIST_TABLE_HEADERS: HeaderType[] = [
    {
      headerText: t('text:Financier_Name'),
    },
    {
      headerText: t('text:Anchor_Master_Agreement_Number'),
    },
    {
      headerText: t('text:Partner_Name'),
    },
    {
      headerText: t('text:Partner_Code_of_Anchor'),
    },
    {
      headerText: t('text:Partner_Master_Agreement_Number'),
    },
    {
      headerText: t('text:Currency'),
    },
    {
      headerText: t('text:BOE_Number'),
    },
    {
      headerText: t('text:Payment_Date'),
    },
    {
      headerText: t('text:Number_of_Invoices'),
    },
    {
      headerText: t('text:Payment_Amount'),
    },
  ];

  return {
    mounted,
    modal,
    t,
    SEARCHED_ANCHOR_INVOICE_PAYMENT_LIST_TABLE_HEADERS,
    AC_INVOICE_PAYMENT_LIST_QS_KEY,
  };
};

function AnchorInvoicePaymentList() {
  const { mounted, modal, t, SEARCHED_ANCHOR_INVOICE_PAYMENT_LIST_TABLE_HEADERS, AC_INVOICE_PAYMENT_LIST_QS_KEY } =
    getConstants();

  const {
    register: anchorInvoicePaymentSearchFormRegister,
    getValues: getAnchorInvoicePaymentSearchFormValues,
    reset: resetAnchorInvoicePaymentSearchFormValue,
    setValue: setAnchorInvoicePaymentSearchFormValue,
    control: anchorInvoicePaymentSearchFormControl,
  } = useForm<AnchorInvoicePaymentListRequest>();

  const getProperty = useProperty<AnchorInvoicePaymentListRequest>();

  const [invoicePaymentPage, setInvoicePaymentPage] = useState<Pageable<InvoicePaymentVOModel[]>>();

  const { pageable: anchorInvoicePaymentPageable, setPageable: setAnchorInvoicePaymentPageable } =
    usePageable(AC_INVOICE_PAYMENT_LIST_QS_KEY);

  const location = useLocation<AnchorInvoicePaymentListRequest>();
  const filteredOptions = location.state;

  useEffect(() => {
    if (mounted) {
      setFormValues<AnchorInvoicePaymentListRequest>(
        setAnchorInvoicePaymentSearchFormValue,
        filteredOptions ?? getParsedSearchParams(AC_INVOICE_PAYMENT_LIST_QS_KEY).formSearchData,
      );
      fetchAll(
        anchorInvoicePaymentPageable.currentPage,
        anchorInvoicePaymentPageable.sizePerPage,
        getAnchorInvoicePaymentSearchFormValues(),
      );
    }
  }, [mounted]);

  const fetchAll = async (
    pageNumber: number = 1,
    rowCount: number = 10,
    searchData: AnchorInvoicePaymentListRequest,
  ) => {
    try {
      const invoicePaymentPage = await requestAnchorInvoicePaymentList(pageNumber, rowCount, searchData);
      updateSearchParams(
        {
          ...searchData,
          pageNumber,
          rowCount,
        },
        AC_INVOICE_PAYMENT_LIST_QS_KEY,
      );
      ReactDOM.unstable_batchedUpdates(() => {
        setInvoicePaymentPage(invoicePaymentPage);
        setAnchorInvoicePaymentPageable(invoicePaymentPage);
      });
    } catch (error) {
      modal.show(error);
    }
  };

  const onClickRemoveFilter = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    resetAnchorInvoicePaymentSearchFormValue();
  };

  const onClickSearch = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    fetchAll(1, anchorInvoicePaymentPageable.sizePerPage, getAnchorInvoicePaymentSearchFormValues());
  };

  const anchorInvoicePaymentPaginate = (pageNumber: number, rowCount: number) => {
    fetchAll(pageNumber, rowCount, getParsedSearchParams(AC_INVOICE_PAYMENT_LIST_QS_KEY).formSearchData);
  };

  const renderSearchedAnchorInvoicePaymentTableBody = () => {
    return invoicePaymentPage?.content.map((data, index) => (
      <Tr key={index}>
        <Td data={data.financierName} />
        <Td data={data.anchorAgreementContractNo} />
        <Td data={data.partnerName} />
        <Td data={data.dealerCodeByAnchor} />
        <Td data={data.partnerAgreementContractNo} />
        <Td className="text-center" data={data.currencyType} />
        <Td data={data.billOfExchangeNo} />
        <Td format="date" data={data.settlementDate} />
        <Td format="number" data={data.totalCount} />
        <Td format="number" data={data.totalAmount} />
      </Tr>
    ));
  };

  return (
    <>
      <HeaderTitle title={t('text:Invoice_Payment')} />
      <div className="content-area">
        <SectionTitle title={t('text:Search')}>
          <Button variant={ButtonVariantEnum.OUTLINED} color={ButtonColorEnum.SECONDARY} onClick={onClickRemoveFilter}>
            {t('text:Remove_Filter')}
          </Button>
        </SectionTitle>
        <form>
          <SearchBorder>
            <div className="row">
              <SearchLabel label={t('text:Financier_Name')} />
              <SearchInput name={getProperty('financierName')} ref={anchorInvoicePaymentSearchFormRegister} />
              <SearchLabel label={t('text:Anchor_Master_Agreement_Number')} />
              <SearchInput
                name={getProperty('anchorAgreementContractNo')}
                ref={anchorInvoicePaymentSearchFormRegister}
              />
            </div>
            <div className="row">
              <SearchLabel label={t('text:Partner_Name')} />
              <SearchInput name={getProperty('partnerName')} ref={anchorInvoicePaymentSearchFormRegister} />
              <SearchLabel label={t('text:Partner_Code_of_Anchor')} />
              <SearchInput name={getProperty('dealerCodeByAnchor')} ref={anchorInvoicePaymentSearchFormRegister} />
            </div>
            <div className="row">
              <SearchLabel label={t('text:Partner_Master_Agreement_Number')} />
              <SearchInput
                name={getProperty('partnerAgreementContractNo')}
                ref={anchorInvoicePaymentSearchFormRegister}
              />
              <SearchLabel label={t('text:Currency')} />
              <SearchSelect
                name={getProperty('currencyType')}
                ref={anchorInvoicePaymentSearchFormRegister}
                selectOptions={getSelectOptions('CURRENCY_TYPE', 'ALL', true)}
              />
            </div>
            <div className="row">
              <SearchLabel label={t('text:BOE_Number')} />
              <SearchInput name={getProperty('billOfExchangeNo')} ref={anchorInvoicePaymentSearchFormRegister} />
              <SearchLabel label={t('text:Payment_Date')} />
              <SearchDatePicker
                name={getProperty('paymentDateFrom')}
                placeholder={t('text:from')}
                control={anchorInvoicePaymentSearchFormControl}
              />
              <SearchDatePicker
                name={getProperty('paymentDateTo')}
                placeholder={t('text:to')}
                control={anchorInvoicePaymentSearchFormControl}
              />
            </div>
          </SearchBorder>
          <div className="flex-center mt-4">
            <Button size={ButtonSizeEnum.LG} onClick={onClickSearch}>
              {t('text:Search')}
            </Button>
          </div>
        </form>
      </div>
      <div className="division-border" />
      <div className="content-area">
        <SectionTitle title={t('text:Result')} />
        <p className="total-data">
          {t('text:Total')} : {anchorInvoicePaymentPageable.totalRows}
        </p>
        <TableBorder>
          <TableHeader header={SEARCHED_ANCHOR_INVOICE_PAYMENT_LIST_TABLE_HEADERS} />
          <TableBody numOfCol={SEARCHED_ANCHOR_INVOICE_PAYMENT_LIST_TABLE_HEADERS.length}>
            {renderSearchedAnchorInvoicePaymentTableBody()}
          </TableBody>
        </TableBorder>
        <Pagination pageable={anchorInvoicePaymentPageable} paginate={anchorInvoicePaymentPaginate} />
      </div>
    </>
  );
}

export default AnchorInvoicePaymentList;
